import axiosInstanceWeb from "./axiosInstance.js";
import axiosInstanceMarvin from "./axiosInstanceMarvin.js";
import { getUserDetails } from "../utils/authUtils";

// Lead-360 config used to be cached by the browser for 15 minutes via HTTP cache
// headers. Security asked us to drop those headers, so we cache the response in
// sessionStorage instead: per-tab, cleared on tab close, and bounded by the same
// 15-minute TTL. Keyed per leadTypeId since the config differs by lead type.
const LEAD360_CACHE_TTL_MS = 15 * 60 * 1000;
// Scoped by org + user so a config cached for one tenant is never served after
// switching tenants in the same tab. Returns null when either is unknown: with no
// identity every tenant would share one label, so nothing is read or written.
const lead360CachePrefix = () => {
  const user = getUserDetails();
  if (!user?.OrgCode || !user?.Id) return null;
  return `lead360:${user.OrgCode}:${user.Id}:`;
};

// Remove every lead360 entry that is not this user's: un-scoped entries from earlier
// versions and whatever another tenant or user left in this tab. Runs once per identity
// instead of on every call.
let purgedForPrefix = null;
const purgeOtherLead360Cache = (prefix) => {
  if (purgedForPrefix === prefix) return;
  purgedForPrefix = prefix;
  try {
    const stale = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("lead360:") && !key.startsWith(prefix)) stale.push(key);
    }
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing to purge.
  }
};

// null when the user is unknown, so nothing is read or written for them.
const lead360CacheKey = (leadTypeId) => {
  const prefix = lead360CachePrefix();
  if (!prefix) return null;
  purgeOtherLead360Cache(prefix);
  return `${prefix}${leadTypeId || "default"}`;
};

const readLead360Cache = (key) => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (!ts || Date.now() - ts > LEAD360_CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    // Wrap so the caller can tell "cache hit" from a cached-but-falsy value.
    return { data };
  } catch {
    // Corrupt entry / storage unavailable — treat as a cache miss.
    return null;
  }
};

const writeLead360Cache = (key, data) => {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // Private mode / quota exceeded — caching is best-effort, so ignore.
  }
};

export const apiGetLead360 = async () => {
  const leadTypeId = await window.lead360HelperUtils?.getLeadTypeId();
  const cacheKey = lead360CacheKey(leadTypeId);

  const cached = readLead360Cache(cacheKey);
  if (cached) {
    // Mirror the axios response shape the caller expects.
    return { status: 200, data: cached.data };
  }

  const response = await axiosInstanceWeb.get(
    `/lead-360/user?leadTypeId=${leadTypeId || "default"}`
  );
  if (response?.status === 200) {
    writeLead360Cache(cacheKey, response.data);
  }
  return response;
};

export const fetchImageUrl = async (key) => {
  try {
    const response = await axiosInstanceWeb.post(`/widgets/signedUrl`, {
      keys: [key],
    });
    return response.data.urls[0]; // Adjust the response to match the actual structure of your API response
  } catch (error) {
    console.error("Error fetching image URL:", error);
    return null; // Return null if there's an error
  }
};

export const fetchTaskCountApi = async (body) => {
  return await axiosInstanceMarvin.post(`Lead/Task/Get`, body);
};

export const fetchOpportunityCountApi = async (body) => {
  return await axiosInstanceMarvin.post(`Activity/Get`, body);
};

export const fetchActivityCountApi = async (body) => {
  return await axiosInstanceMarvin.post(`Activity/Get`, body);
};

export const fetchMailMergedContent = async (body) => {
  return await axiosInstanceMarvin.post(`/Connector/MailMergedContent/Get`, body);
};
